import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle, AlertTriangle, Eye, ImageOff, LayoutGrid, List, Loader2, RotateCcw, Upload } from "lucide-react";
import {
  useGetV4ManufacturerByRouteManufacturerIdAccountQuery,
  useGetV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeQuery,
  usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeFieldNameMutation,
  usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeImageMutation,
} from "randmar-api-client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ActiveOrdersCard,
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card, CardContent, CardHeader, CardTitle,
  DataTable,
  Input,
  Label,
  OrdersCard,
  ProductCard,
  SalesChart,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  formatMoney,
  useToast,
} from "randmarcomps";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NewMarginsGrid from "../components/grids/NewMarginsGrid";
import ProductActions from "../components/products/ProductActions";
import Path from "../routes/Path";
import { Product } from "../stores/product/Product";

const CategoryImagePlaceholder = () => (
  <div
    className="absolute inset-0 bg-muted flex flex-col items-center justify-center text-muted-foreground"
    aria-label="Image not available"
  >
    <ImageOff className="h-12 w-12" />
    <p className="mt-2 text-sm">No Image</p>
  </div>
);

const ProductTableImage = ({ sku, alt }: { sku: string; alt: string }) => {
  const [hasError, setHasError] = useState(false);
  const timestamp = useRef(new Date().getTime());
  const imageUrl = `${Path.productImage(sku)}?t=${timestamp.current}`;

  if (hasError || !sku) {
    return (
      <div className="h-12 w-12 flex-shrink-0 bg-muted rounded-sm flex items-center justify-center text-muted-foreground">
        <ImageOff className="h-6 w-6" />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className="h-12 w-12 flex-shrink-0 rounded-sm object-contain"
      onError={() => setHasError(true)}
    />
  );
};

export default function CategoryPage() {
  const { applicationId, categoryCode } = useParams<{ applicationId: string; categoryCode: string }>();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    data: account,
    isLoading: isAccountLoading,
  } = useGetV4ManufacturerByRouteManufacturerIdAccountQuery(
    { routeManufacturerId: applicationId! },
    { skip: !applicationId }
  );

  const {
    data: category,
    isLoading,
    isError,
    refetch,
  } = useGetV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeQuery(
    { routeManufacturerId: applicationId!, categoryCode: categoryCode! },
    { skip: !categoryCode || !applicationId }
  );

  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageExists, setImageExists] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [activeTab, setActiveTab] = useState("statistics");
  const [productView, setProductView] = useState<'grid' | 'table'>('grid');
  const [sortOption, setSortOption] = useState<string>("percentile"); // State for grid view sorting

  const [formData, setFormData] = useState({
    CategoryName: category?.CategoryName ?? '',
    Niche: category?.Niche ?? '',
    Starred: category?.Starred ?? false,
    AutoReorder: category?.AutoReorder ?? false,
    QualificationRequired: category?.QualificationRequired ?? false,
    MinimumOrder: category?.MinimumOrder ?? null,
    LeadTime: category?.LeadTime ?? null,
    WeeksOfStocking: category?.WeeksOfStocking ?? null,
  });

  const activeOrders = useMemo(() => category?.ActiveOrderDetails ?? [], [category]);
  const products = useMemo(() => category?.Products ?? [], [category]);
  const statistics = useMemo(() => category?.SalesStatistics ?? [], [category]);
  const operationValues = useMemo(() => {
    if (!category?.InventoryValue?.length) return null;
    const data = category.InventoryValue;
    const totals = data.reduce(
      (acc, v) => {
        acc.inventory += v.InventoryValue || 0;
        acc.purchaseOrder += v.PurchaseOrderValue || 0;
        acc.salesOrder += v.SalesOrderValue || 0;
        return acc;
      },
      { inventory: 0, purchaseOrder: 0, salesOrder: 0 }
    );
    return { totals, byLocation: data };
  }, [category?.InventoryValue]);

  const hasCustomReorderSettings = useMemo(
    () =>
      category?.MinimumOrder != null ||
      category?.LeadTime != null ||
      category?.WeeksOfStocking != null,
    [category]
  );

  // Memoized sorted products for the grid view
  const sortedProducts = useMemo(() => {
    return [...products].sort((a, b) => {
      switch (sortOption) {
        case "mpn-asc":
          return (a.MPN ?? "").localeCompare(b.MPN ?? "");
        case "mpn-desc":
          return (b.MPN ?? "").localeCompare(a.MPN ?? "");
        case "price-asc":
          return (a.MAP ?? 0) - (b.MAP ?? 0);
        case "price-desc":
          return (b.MAP ?? 0) - (a.MAP ?? 0);
        case "name":
          return (a.Title ?? "").localeCompare(b.Title ?? "");
        case "percentile":
          return (b.OverallPercentileRank ?? 0) - (a.OverallPercentileRank ?? 0);
        default:
          return 0;
      }
    });
  }, [products, sortOption]);

  const productColumns = useMemo((): ColumnDef<(typeof products)[0]>[] => [
    {
      accessorKey: "Title",
      header: "Product",
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="flex items-center gap-4">
            <ProductTableImage sku={product.RandmarSKU!} alt={product.Title ?? 'Product image'} />
            <a href={`/${applicationId}/Product/${product.RandmarSKU}`} className="font-medium truncate hover:underline">
              {product.Title}
            </a>
          </div>
        );
      },
    },
    {
      accessorKey: "RandmarSKU",
      header: "SKU",
    },
    {
      accessorKey: "MPN",
      header: "MPN",
      cell: ({ row }) => row.original.MPN ?? '—',
    },
    {
      accessorKey: "MAP",
      header: "MAP",
      cell: ({ row }) => row.original.MAP ? formatMoney(row.original.MAP) : '—',
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <>
          <Link to={Path.product(applicationId, row.original.RandmarSKU ?? "")}>
            <Button className="mr-2" variant="secondary">
              <Eye className="h-5 w-5" />
              <span>View</span>
            </Button>
          </Link>
          <ProductActions
            product={Product.convertFromNewProduct(row.original)}
          />
        </>
      ),
    },
  ], [applicationId]);

  useEffect(() => {
    if (category) {
      setFormData({
        CategoryName: category.CategoryName ?? '',
        Niche: category.Niche ?? '',
        Starred: category.Starred ?? false,
        AutoReorder: category.AutoReorder ?? false,
        QualificationRequired: category.QualificationRequired ?? false,
        MinimumOrder: category.MinimumOrder ?? null,
        LeadTime: category.LeadTime ?? null,
        WeeksOfStocking: category.WeeksOfStocking ?? null,
      });
    }
  }, [category]);

  useEffect(() => {
    if (category?.CategoryCode) {
      setImageUrl(`${Path.categoryImage(applicationId!, category.CategoryCode)}?t=${new Date().getTime()}`);
    }
  }, [category, applicationId]);

  const [updateCategoryField] = usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeFieldNameMutation();
  const [updateCategoryImage, { isLoading: isImageUpdating }] = usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeImageMutation();

  const handleImageFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      await updateCategoryImage({
        routeManufacturerId: applicationId!,
        categoryCode: category!.CategoryCode!,
        body: { file },
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Category image updated." });
      setImageUrl(`${Path.categoryImage(applicationId!, category!.CategoryCode!)}?t=${new Date().getTime()}`);
    } catch (error) {
      toast({ variant: "destructive", title: "Image Upload Failed", description: "Please ensure the file is a valid image." });
    }
  };

  const handleInputChange = (fieldName: keyof typeof formData, value: string | number | boolean | null) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const hasChanges = useMemo(() => {
    if (!category) return false;
    return (
      formData.CategoryName !== (category.CategoryName ?? "") ||
      formData.Niche !== (category.Niche ?? "") ||
      formData.Starred !== (category.Starred ?? false) ||
      formData.AutoReorder !== (category.AutoReorder ?? false) ||
      formData.QualificationRequired !== (category.QualificationRequired ?? false) ||
      (formData.MinimumOrder ?? null) !== (category.MinimumOrder ?? null) ||
      (formData.LeadTime ?? null) !== (category.LeadTime ?? null) ||
      (formData.WeeksOfStocking ?? null) !== (category.WeeksOfStocking ?? null)
    );
  }, [formData, category]);

  const handleSave = async () => {
    if (!hasChanges) return;
    setIsSaving(true);
    const changedFields: { fieldName: string; newValue: string }[] = [];

    (Object.keys(formData) as Array<keyof typeof formData>).forEach((key) => {
      let originalValue: string | number | boolean | null;
      switch (key) {
        case 'Starred':
        case 'AutoReorder':
        case 'QualificationRequired':
          originalValue = category?.[key] ?? false;
          break;
        case 'MinimumOrder':
        case 'LeadTime':
        case 'WeeksOfStocking':
          originalValue = category?.[key] ?? null;
          break;
        default:
          originalValue = category?.[key] ?? '';
          break;
      }

      if (formData[key] !== originalValue) {
        const newValueString = formData[key] === null ? '' : String(formData[key]);
        changedFields.push({ fieldName: key, newValue: newValueString });
      }
    });

    if (changedFields.length === 0) {
      setIsSaving(false);
      return;
    }

    const savePromises = changedFields.map(field =>
      updateCategoryField({
        routeManufacturerId: applicationId!,
        categoryCode: category!.CategoryCode!,
        fieldName: field.fieldName,
        newValue: field.newValue,
      }).unwrap()
    );

    try {
      await Promise.all(savePromises);
      toast({ variant: 'success', title: 'Success', description: 'Changes saved successfully.' });
      refetch();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Save Failed', description: 'One or more fields could not be saved.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetReorderSettings = async () => {
    setIsResetting(true);
    const fieldsToReset: ('MinimumOrder' | 'LeadTime' | 'WeeksOfStocking')[] = [];
    if (category?.MinimumOrder != null) fieldsToReset.push('MinimumOrder');
    if (category?.LeadTime != null) fieldsToReset.push('LeadTime');
    if (category?.WeeksOfStocking != null) fieldsToReset.push('WeeksOfStocking');

    if (fieldsToReset.length === 0) {
      setIsResetting(false);
      return;
    }

    const resetPromises = fieldsToReset.map((fieldName) =>
      updateCategoryField({
        routeManufacturerId: applicationId!,
        categoryCode: category!.CategoryCode!,
        fieldName: fieldName,
        newValue: '',
      }).unwrap()
    );

    try {
      await Promise.all(resetPromises);
      toast({ variant: 'success', title: 'Success', description: 'Reorder settings reset to manufacturer defaults.' });
      refetch();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Reset Failed', description: 'Could not reset reorder settings.' });
    } finally {
      setIsResetting(false);
    }
  };

  if (isLoading || isAccountLoading) {
    return (
      <div className="space-y-4 p-4 md:p-6">
        <Skeleton className="h-10 w-1/3" />
        <div className="flex gap-6">
          <div className="w-1/3"><Skeleton className="h-64 w-full" /></div>
          <div className="w-2/3"><Skeleton className="h-64 w-full" /></div>
        </div>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError || !category) {
    return (
      <Card className="m-4 p-6 md:m-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Could not load category details. Please try again.</AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">{category.CategoryName}</h1>
      </header>

      <div className="flex grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8">
        <div className="w-full">
          <div className="aspect-square bg-muted rounded-md flex items-center justify-center mb-4 relative overflow-hidden">
            {!imageExists && <CategoryImagePlaceholder />}
            <img
              src={imageUrl}
              alt={imageExists ? `Image of ${category.CategoryName}` : ""}
              className={`w-full h-full object-contain transition-opacity duration-300 ${imageExists ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImageExists(true)}
              onError={() => setImageExists(false)}
            />
          </div>
          <input
            id="categoryImage"
            type="file"
            ref={fileInputRef}
            onChange={handleImageFileChange}
            className="hidden"
            accept="image/*"
          />
          <Button
            className="w-full"
            onClick={() => fileInputRef.current?.click()}
            disabled={isImageUpdating}
          >
            {isImageUpdating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            {isImageUpdating ? 'Uploading...' : 'Upload Image'}
          </Button>
        </div>

        <div className="w-full @container sm:col-span-2 md:col-span-3 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-bold">Category Information & Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 @2xl:grid-cols-2 gap-x-8 gap-y-6">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="categoryName">Name</Label>
                    <Input id="categoryName" value={formData.CategoryName} onChange={(e) => handleInputChange('CategoryName', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="niche">Niche</Label>
                    <Input id="niche" value={formData.Niche} onChange={(e) => handleInputChange('Niche', e.target.value)} />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="starred-switch" className="pr-2">Starred Category</Label>
                    <Switch id="starred-switch" checked={formData.Starred} onCheckedChange={(checked) => handleInputChange('Starred', checked)} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="reorder-switch" className="pr-2">Auto Reorder</Label>
                    <Switch id="reorder-switch" checked={formData.AutoReorder} onCheckedChange={(checked) => handleInputChange('AutoReorder', checked)} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="qualified-switch" className="pr-2">Qualified Resellers Only</Label>
                    <Switch id="qualified-switch" checked={formData.QualificationRequired} onCheckedChange={(checked) => handleInputChange('QualificationRequired', checked)} />
                  </div>
                </div>
              </div>
              <Accordion type="single" collapsible className="w-full pt-6" defaultValue={hasCustomReorderSettings ? "reorder-settings" : undefined}>
                <AccordionItem value="reorder-settings" className="border rounded-md px-4">
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Reorder Settings</span>
                      {hasCustomReorderSettings && (
                        <div className="flex items-center gap-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          <AlertTriangle className="h-3 w-3" />
                          Category Override
                        </div>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 @lg:grid-cols-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="weeksOfStocking">Weeks of Stock</Label>
                        <Input
                          id="weeksOfStocking"
                          type="number"
                          placeholder={`Default: ${account?.DefaultWeeksOfStocking ?? '...'}`}
                          value={formData.WeeksOfStocking ?? ''}
                          onChange={(e) => handleInputChange('WeeksOfStocking', e.target.value === '' ? null : e.target.valueAsNumber)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="leadTime">Lead Time (days)</Label>
                        <Input
                          id="leadTime"
                          type="number"
                          placeholder={`Default: ${account?.DefaultLeadTime ?? '...'}`}
                          value={formData.LeadTime ?? ''}
                          onChange={(e) => handleInputChange('LeadTime', e.target.value === '' ? null : e.target.valueAsNumber)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="minimumOrder">Minimum Order ($)</Label>
                        <Input
                          id="minimumOrder"
                          type="number"
                          placeholder={`Default: ${account?.DefaultMinimumOrder ?? '...'}`}
                          value={formData.MinimumOrder ?? ''}
                          onChange={(e) => handleInputChange('MinimumOrder', e.target.value === '' ? null : e.target.valueAsNumber)}
                        />
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleResetReorderSettings}
                        disabled={!hasCustomReorderSettings || isResetting}
                      >
                        {isResetting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                        Reset to Defaults
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              <div className="flex justify-end pt-8">
                <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Save
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="w-full sm:col-span-2 md:col-span-4 lg:col-span-2">
          <ActiveOrdersCard orders={activeOrders} />
        </div>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="inline-flex h-auto items-center justify-center rounded-none border-b bg-transparent p-0">
          <TabsTrigger
            value="statistics" disabled={statistics.length === 0}
            className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none hover:text-foreground data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none cursor-pointer"
          >
            Statistics
          </TabsTrigger>
          <TabsTrigger
            value="margins"
            className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none hover:text-foreground data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none cursor-pointer"
          >
            Margins
          </TabsTrigger>
          <TabsTrigger
            value="operations" disabled={!operationValues}
            className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none hover:text-foreground data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none cursor-pointer"
          >
            Operations
          </TabsTrigger>
          <TabsTrigger
            value="orders" disabled={activeOrders.length === 0}
            className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none hover:text-foreground data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none cursor-pointer"
          >
            Active Orders ({activeOrders.length})
          </TabsTrigger>
          <TabsTrigger
            value="products" disabled={products.length === 0}
            className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none hover:text-foreground data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none cursor-pointer"
          >
            Products ({products.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="statistics" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Sales Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <SalesChart statistics={statistics} chartHeight={300} />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="products" className="mt-4">
          <TooltipProvider>
            <div className="flex items-center gap-4 my-4">
              {productView === 'grid' && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Sort by:
                  </label>
                  <Select value={sortOption} onValueChange={setSortOption}>
                    <SelectTrigger className="w-[220px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentile">Best Selling</SelectItem>
                      <SelectItem value="name">Name</SelectItem>
                      <Separator />
                      <SelectItem value="price-asc">MAP: Low to High</SelectItem>
                      <SelectItem value="price-desc">MAP: High to Low</SelectItem>
                      <Separator />
                      <SelectItem value="mpn-asc">MPN: Low to High</SelectItem>
                      <SelectItem value="mpn-desc">MPN: High to Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center rounded-md border bg-muted p-0.5 ml-auto">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant={productView === 'grid' ? 'secondary' : 'ghost'} size="icon" className="h-8 w-8" onClick={() => setProductView('grid')}>
                      <LayoutGrid className="h-4 w-4" />
                      <span className="sr-only">Grid View</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Grid View</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant={productView === 'table' ? 'secondary' : 'ghost'} size="icon" className="h-8 w-8" onClick={() => setProductView('table')}>
                      <List className="h-4 w-4" />
                      <span className="sr-only">Table View</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Table View</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </TooltipProvider>

          {productView === 'grid' ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {sortedProducts.map((product) => (
                <ProductCard
                  applicationId={applicationId!}
                  key={`product_grid_${product.RandmarSKU}`}
                  product={product}
                  viewProductLink={`/${applicationId}/Product/${product.RandmarSKU}`}
                  customAction={
                    <ProductActions
                      key={`product_actions_grid_${product.RandmarSKU}`}
                      product={Product.convertFromNewProduct(product)}
                    />
                  }
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Products</CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  columns={productColumns}
                  data={products}
                  uniqueValues={{}}
                  initialSort={[{ id: "Title", desc: false }]}
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="operations" className="mt-4">
          {operationValues && (
            <Card>
              <CardHeader>
                <CardTitle>Location Details</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead />
                      <TableHead className="text-center">Total</TableHead>
                      {["MTL", "TOR", "VAN", "EDM"].map((loc) => (
                        <TableHead key={loc} className="text-center">{loc}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-semibold">Inventory Value</TableCell>
                      <TableCell className="text-center font-semibold">{formatMoney(operationValues.totals.inventory)}</TableCell>
                      {["MTL", "TOR", "VAN", "EDM"].map((loc) => (
                        <TableCell key={loc} className="text-center">
                          {formatMoney(operationValues.byLocation?.find(d => d.Location === loc)?.InventoryValue || 0)}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-semibold">On Purchase Orders</TableCell>
                      <TableCell className="text-center font-semibold">{formatMoney(operationValues.totals.purchaseOrder)}</TableCell>
                      {["MTL", "TOR", "VAN", "EDM"].map((loc) => (
                        <TableCell key={loc} className="text-center">{formatMoney(operationValues.byLocation?.find(d => d.Location === loc)?.PurchaseOrderValue || 0)}</TableCell>))}</TableRow><TableRow><TableCell className="font-semibold">On Sales Orders</TableCell><TableCell className="text-center font-semibold">{formatMoney(operationValues.totals.salesOrder)}</TableCell>{["MTL", "TOR", "VAN", "EDM"].map((loc) => (<TableCell key={loc} className="text-center">{formatMoney(operationValues.byLocation?.find(d => d.Location === loc)?.SalesOrderValue || 0)}</TableCell>
                        ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="margins" className="mt-4">
          <Card>
            <NewMarginsGrid category={category} />
          </Card>
        </TabsContent>
        <TabsContent value="orders" className="mt-4">
          <OrdersCard applicationId={applicationId ?? ""} orders={activeOrders} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
